import React, { useContext } from "react";
import { useState } from "react";
import { LoadingContext, QuestionsContext, ReviewContext } from "./MyContexts";
import { Reviews, ReviewState, StringObj } from "../type";
import { reviewTypeValues } from "../taskpane/features/review/config";
import { getReviewByLLM, invokeProcessLog } from "../commands/api";
import { loadSheetValues } from "../commands/questions/excelInfo";
import { logicalCheck } from "../commands/review/logical";
import { get } from "lodash";

function getCurrentTimestamp(): string {
  const now = new Date();

  const pad = (num: number): string => num.toString().padStart(2, "0");

  const yy = pad(now.getFullYear() % 100); // 下2桁の年
  const MM = pad(now.getMonth() + 1); // 月（0始まりなので+1）
  const dd = pad(now.getDate()); // 日
  const HH = pad(now.getHours()); // 時
  const mm = pad(now.getMinutes()); // 分
  const ss = pad(now.getSeconds()); // 秒

  return `${yy}${MM}${dd}${HH}${mm}${ss}`;
}

export default function ReviewProvider(props: { children: React.ReactNode }) {
  const [reviews, setReviews] = useState<Reviews>({
    excel: [],
    llm: [],
  });

  const [reviewFiles, setReviewFiles] = useState<{
    excel: string;
    llm: string;
  }>({
    excel: "",
    llm: "",
  });

  function handleSetReviews(newReviews: Reviews) {
    setReviews(newReviews);
  }

  function handleSetReview(newReview: StringObj[], type: string) {
    setReviews((prev) => {
      return {
        ...prev,
        [type]: newReview,
      };
    });
  }

  const [reviewState, setReviewState] = useState<ReviewState>("");

  function handleReviewState(state: ReviewState) {
    setReviewState(state);
  }

  const [reviewTypes, setReviewTypes] = useState<string[]>(reviewTypeValues.map((i) => i.id));

  function handleChangeReviewType(id: string) {
    if (reviewTypes.includes(id)) {
      setReviewTypes(reviewTypes.filter((i) => i !== id));
    } else {
      setReviewTypes([...reviewTypes, id]);
    }
  }

  function handleChangeReviewTypeAll() {
    if (reviewTypes.length === reviewTypeValues.length) {
      setReviewTypes([]);
    } else {
      setReviewTypes(reviewTypeValues.map((i) => i.id));
    }
  }

  const [reviewTargets, setReviewTargets] = useState<number[]>([]);

  function handleChangeReviewTarget(index: number) {
    if (reviewTargets.includes(index)) {
      setReviewTargets(reviewTargets.filter((i) => i !== index));
    } else {
      setReviewTargets([...reviewTargets, index]);
    }
  }

  function handleChangeReviewTargetAll(questions: any[]) {
    if (reviewTargets.length === questions.length) {
      setReviewTargets([]);
    } else {
      setReviewTargets(questions.map((_, i) => i + 1));
    }
  }

  function downloadReviewResults(reviewType: "excel" | "llm") {
    const yymmddhhddss = getCurrentTimestamp();
    const filename = `FORMNAME_${reviewType}_${yymmddhhddss}.json`;

    const data = reviews[reviewType];
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: "application/json" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();

    URL.revokeObjectURL(url);
  }

  function uploadReviewResults(file: File, reviewType: "excel" | "llm") {
    reviewType;
    const reader = new FileReader();
    reader.onload = (event) => {
      const jsonString = event.target?.result as string;
      const filename = file.name;
      try {
        const data = JSON.parse(jsonString);
        setReviews((prev) => {
          return {
            ...prev,
            [reviewType]: data,
          };
        });
        setReviewFiles((prev) => {
          return {
            ...prev,
            [reviewType]: filename,
          };
        });
      } catch (error) {
        console.error("Error parsing JSON:", error);
      }
    };
    reader.readAsText(file);
  }

  function removeReviewFile(reviewType: "excel" | "llm") {
    setReviewFiles((prev) => {
      return {
        ...prev,
        [reviewType]: "",
      };
    });
    setReviews((prev) => {
      return {
        ...prev,
        [reviewType]: [],
      };
    });
  }

  async function handleDismiss(id: string, type: "excel" | "llm") {
    await setReviews((prev) => {
      return {
        ...prev,
        [type]: prev[type].map((i) => {
          if (i.id === id) {
            return {
              ...i,
              dismiss: !(i.dismiss || false),
            };
          } else {
            return i;
          }
        }),
      };
    });
    moveNeighborReview("next", true);
  }

  async function handleDone(id: string, type: "excel" | "llm") {
    await setReviews((prev) => {
      return {
        ...prev,
        [type]: prev[type].map((i) => {
          if (i.id === id) {
            return {
              ...i,
              done: !(i.done || false),
            };
          } else {
            return i;
          }
        }),
      };
    });
    moveNeighborReview("next", true);
  }

  function updateComments(id: string, type: "excel" | "llm", comments: StringObj[]) {
    setReviews((prev) => {
      return {
        ...prev,
        [type]: prev[type].map((i) => {
          if (i.id === id) {
            return {
              ...i,
              comments: comments,
            };
          } else {
            return i;
          }
        }),
      };
    });
  }

  const [isExtraLoading, setIsExtraLoading] = useState<boolean>(false);

  const { handleLoadingOpen, handleLoadingClose } = useContext(LoadingContext);
  const { questions, editSheet, setQuestions, updateEditSheet } = useContext(QuestionsContext);

  const step2Options = ["option", "random", "condition"];

  async function llmReview(sheet: string, step: number, prev: any[] = [], option: string = "") {
    if (step == 1) {
      handleLoadingOpen("box", `${sheet}の調査票の内容をレビューしています...`);
    }

    handleReviewState("llm");
    console.log(questions);
    const questionIds = questions
      .filter((_, index) => {
        return reviewTargets.includes(+index + 1);
      })
      .map((q) => {
        // q.questionIdのなかにアルファベットがある場合はそのまま、ない場合は先頭にQを付ける
        return q.questionId.match(/[a-zA-Z]/) ? q.questionId : "Q" + q.questionId;
      });

    const reviewOptions =
      step == 1
        ? reviewTypes.filter((i) => !step2Options.includes(i))
        : reviewTypes.filter((i) => step2Options.includes(i));

    if (step === 1) {
      const reviewsTmp = reviewOptions.length
        ? await getReviewByLLM(questions, reviewOptions, questionIds)
        : [];

      const newValue = reviewsTmp.map((elm, i) => ({
        ...elm,
        id: "1_" + i.toString(),
      }));

      handleSetReview(newValue, "llm");
      handleLoadingClose();
      return newValue as any[];
    } else {
      if (option) {
        const result = await getReviewByLLM(questions, [option], questionIds);
        const withId = result.map((elm, i) => ({
          ...elm,
          id: `2_${prev.length + i}`,
        }));
        const newValue = [...prev, ...withId];
        handleSetReview(newValue, "llm");
        return newValue;
      } else {
        return prev; // オプションがない場合は前の結果をそのまま返す
      }
    }
  }

  async function handleLLMReview() {
    handleReviewState("llm");

    const reviewOptions = [
      reviewTypes.filter((i) => !step2Options.includes(i)),
      reviewTypes.filter((i) => step2Options.includes(i)),
    ];

    let curValue = reviewOptions[0].length ? await llmReview(editSheet, 1) : [];
    setIsExtraLoading(true);

    for (const option of reviewOptions[1]) {
      console.log(option);
      const curValueNew = await llmReview(editSheet, 2, curValue, option);
      curValue = curValueNew;
    }

    setIsExtraLoading(false);
  }

  async function handleExcelReview() {
    handleLoadingOpen("box", `${editSheet}の調査票のフォーマットをレビューしています...`);
    handleReviewState("excel");

    const { questions, checkResultsExcel } = await loadSheetValues(editSheet, "B1:N10000");

    console.log(checkResultsExcel);
    const questionIds = questions
      .filter((_, index) => {
        return reviewTargets.includes(+index + 1);
      })
      .map((q) => {
        return q.questionId.match(/[a-zA-Z]/) ? q.questionId : "Q" + q.questionId;
      });

    const checkResultsLogical = await logicalCheck(questions);
    console.log(checkResultsLogical);

    const newReviews = [...checkResultsExcel, ...checkResultsLogical]
      .filter((elm) =>
        get(elm, "questionId", "")
          ? questionIds.includes(elm["questionId"]) || questionIds.includes(`Q${elm["questionId"]}`)
          : true
      )
      .map((elm, i) => {
        return {
          ...elm,
          id: i.toString(),
        };
      });

    handleSetReview(newReviews, "excel");
    await invokeProcessLog({
      questions: questions,
      checkResults: newReviews,
    });
    handleLoadingClose();
  }

  const [selectedId, setSelectedId] = useState<string>("");

  function handleSelectId(id: string) {
    setSelectedId((prev) => {
      if (prev === id) {
        return "";
      } else {
        return id;
      }
    });
  }

  function moveNeighborReview(to: "next" | "prev", filter: boolean = false) {
    const targetReviews = filter
      ? reviews[reviewState].filter((r) => !r.dismiss && !r.done)
      : reviews[reviewState];

    const curIndex = targetReviews.findIndex((r) => r.id === selectedId);

    const nextId =
      curIndex == targetReviews.length - 1 ? targetReviews[0].id : targetReviews[curIndex + 1].id;
    const prevId =
      curIndex === 0 ? targetReviews[targetReviews.length - 1].id : targetReviews[curIndex - 1].id;

    if (to === "next") {
      handleSelectId(nextId);
    } else {
      handleSelectId(prevId);
    }
  }

  function resetState() {
    setReviews({
      excel: [],
      llm: [],
    });
    setReviewFiles({
      excel: "",
      llm: "",
    });
    setReviewState(undefined);
    setReviewTypes(reviewTypeValues.map((i) => i.id));
    setReviewTargets([]);
    setSelectedId("");

    setQuestions([]);
    updateEditSheet("");
  }

  return (
    <ReviewContext.Provider
      value={{
        reviews,
        handleSetReview,
        handleSetReviews,
        reviewState,
        handleReviewState,
        reviewTypes,
        handleChangeReviewType,
        handleChangeReviewTypeAll,
        reviewTargets,
        handleChangeReviewTarget,
        handleChangeReviewTargetAll,
        downloadReviewResults,
        reviewFiles,
        uploadReviewResults,
        removeReviewFile,
        handleDismiss,
        handleDone,
        updateComments,
        handleExcelReview,
        handleLLMReview,
        isExtraLoading,
        selectedId,
        handleSelectId,
        moveNeighborReview,
        resetState,
      }}
    >
      {props.children}
    </ReviewContext.Provider>
  );
}
